import { useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { CheckmarkOutline, Upload, Warning } from '@mint-lib/icons';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import { Alert } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import {
  BackButton,
  DiscardButton,
  Switch,
} from '../../../components/atoms/index.js';
import { RichTooltip } from '../../../components/atoms/RichTooltip.jsx';
import { ErrorsFoundLabel } from '../../../components/molecules/ErrorsFoundLabel.jsx';
import { NavbarSlot } from '../../../components/molecules/NavbarSlot.jsx';
import { RowsDiscardConfirmationDialog } from '../../../components/molecules/RowsDiscardConfirmationDialog.jsx';
import { ValidationProgressState } from '../../../components/molecules/ValidationProgressState.jsx';
import { DataConfigurationTable } from '../../../components/organisms/DataConfigurationTable.jsx';
import { useDataFileContext } from '../../../contexts/DataFileContext/useDataFileContext.js';
import { useNativeMetricsPlatformMediaRow } from '../../../hooks/ff/index.js';
import { useDiscard } from '../../../hooks/useDiscard.js';
import { useDataAssignmentRouteApi } from '../../../hooks/useImportHubRouteApi.js';
import {
  useDiscardConfiguredRows,
  useEditDataConfigurationField,
} from '../../../queries/index.js';
import { useValidatedData } from './useValidatedData.js';

export const DataConfigurationStep = () => {
  const { t } = useTranslation('@myn/import-hub');
  const isNativeMetricsFFEnabled = useNativeMetricsPlatformMediaRow();

  const dataAssignmentRoute = useDataAssignmentRouteApi();
  const navigate = dataAssignmentRoute.useNavigate();
  const searchParams = dataAssignmentRoute.useSearch();

  const {
    dataFileId,
    goToNextStep,
    goToPrevStep,
    isSubmitting,
    showUploadFileDialog,
  } = useDataFileContext();

  const { mutateAsync: discardDataFileRows, isPending: isDiscarding } =
    useDiscardConfiguredRows(dataFileId, searchParams);

  const {
    selection,
    selectedRowsIds,
    hasSelection,
    resetSelection,
    handleSelectAllRows,
    handleSelectRow,
    discardRows,
  } = useDiscard({
    dataFileId,
    searchParams: searchParams,
    discardDataFileRows,
  });

  const {
    isValidating,
    validationState,
    dataFilePreview,
    data: { rawData, rows, headers, headersMeta },
    errorsCount,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useValidatedData(dataFileId, searchParams, {
    enabled: !isDiscarding,
  });

  const { mutate: editField } = useEditDataConfigurationField(
    dataFileId,
    searchParams,
  );

  const isPlatformCampaignLevel =
    dataFilePreview.granularityLevel === 'platform_campaign';

  const hasRows = Boolean(rows.length);
  const hasErrors = Boolean(errorsCount);

  const isValid = !isLoading && !hasErrors && !dataFilePreview.isEdited;

  useEffect(() => {
    if (isValid && searchParams.onlyErrors) {
      navigate({
        search: (prev) => ({ ...prev, onlyErrors: false }),
      });
    }
  }, [isValid, navigate, searchParams.onlyErrors]);

  const [discardConfirmationDialogIsOpen, setDiscardConfirmationDialogIsOpen] =
    useState(false);
  const showDiscardConfirmationDialog = () => {
    setDiscardConfirmationDialogIsOpen(true);
  };
  const submitDiscardConfirmationDialog = () => {
    discardRows();
    setDiscardConfirmationDialogIsOpen(false);
  };

  const handleShowOnlyRowsWithErrorsChange = (checked: boolean) => {
    resetSelection();
    navigate({
      search: (prev) => ({ ...prev, onlyErrors: checked }),
    });
  };

  const isNextButtonDisabled =
    isLoading || isDiscarding || !hasRows || hasErrors || isSubmitting;

  const actions = (
    <>
      <Button
        onClick={showUploadFileDialog}
        variant="text"
        disabled={isLoading || isSubmitting}
        data-dd-action-name="Replace file"
      >
        <Upload />
        {t('Replace file')}
      </Button>
      <BackButton
        onClick={goToPrevStep}
        disabled={isValidating || isSubmitting}
      />
      <RichTooltip
        description={
          isPlatformCampaignLevel
            ? t(
                'Edit error-containing rows or delete them before completing the import.',
              )
            : t(
                'To proceed, map all unassigned channels to their corresponding platforms.',
              )
        }
        side="left"
        disabled={!hasErrors}
      >
        <div>
          {isValid ? (
            <Button
              onClick={goToNextStep}
              disabled={isNextButtonDisabled}
              data-dd-action-name="Finish import"
            >
              {t('Finish import')}
            </Button>
          ) : (
            <Button
              onClick={goToNextStep}
              disabled={isNextButtonDisabled}
              data-dd-action-name="Validate"
            >
              {t('Validate')}
            </Button>
          )}
        </div>
      </RichTooltip>
    </>
  );

  if (isValidating) {
    return (
      <>
        <NavbarSlot>{actions}</NavbarSlot>
        <ValidationProgressState validationState={validationState} />
      </>
    );
  }

  return (
    <>
      <NavbarSlot>{actions}</NavbarSlot>
      <h4 className={cn(typographyVariants({ variant: 'h4' }), 'mb-2')}>
        {t('Data assignment')}
      </h4>
      <p className={cn(typographyVariants({ variant: 'bodyLong01' }), 'mb-6')}>
        {isPlatformCampaignLevel
          ? t(
              'Assign Campaign ID to each platform based on the references provided in the selected campaign.',
            )
          : t(
              'Assign a channel to each platform based on the references provided in the selected campaign.',
            )}
      </p>
      {(isNativeMetricsFFEnabled || isPlatformCampaignLevel) && (
        <Alert
          severity="info"
          variant="inline"
          description={t(
            'Metrics columns are not displayed in the Data assignment step, since they are validated in the Data check step.',
          )}
          removable={false}
          className="mb-4"
        />
      )}
      <div className="mb-4 flex items-center gap-2">
        <DiscardButton
          onDiscard={showDiscardConfirmationDialog}
          disabled={!hasSelection || isDiscarding}
        />
        <div className="ml-auto flex items-center gap-4">
          {hasErrors ? (
            <ErrorsFoundLabel
              className="text-error-dark"
              message={t('Rows with errors found')}
              icon={Warning}
            />
          ) : (
            !dataFilePreview.isEdited && (
              <ErrorsFoundLabel
                className="text-success-dark"
                message={t('No errors found')}
                icon={CheckmarkOutline}
              />
            )
          )}
          {(hasErrors || searchParams.onlyErrors) && (
            <Switch
              data-dd-action-name="Data assignment: show only errors"
              label={t('Show only errors')}
              checked={Boolean(searchParams.onlyErrors)}
              onCheckedChange={handleShowOnlyRowsWithErrorsChange}
              size="small"
            />
          )}
        </div>
      </div>
      <DataConfigurationTable
        {...{
          dataFilePreview,
          data: rawData,
          headers,
          headersMeta,
          rows,
          selection,
          selectedRowsIds,
          handleSelectAllRows,
          handleSelectRow,
          isLoading,
          isFetchingNextPage,
          itemsPerPage: searchParams.itemsPerPage,
          hasNextPage,
          fetchNextPage,
          editField,
        }}
      />
      {discardConfirmationDialogIsOpen && (
        <RowsDiscardConfirmationDialog
          onClose={() => setDiscardConfirmationDialogIsOpen(false)}
          onSubmit={submitDiscardConfirmationDialog}
        />
      )}
    </>
  );
};
